// src/pages/products/EditProductPage/EditProductPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Package, 
  X, 
  AlertCircle, 
  Info,
  DollarSign,
  Tag,
  Settings,
  Image as ImageIcon
} from 'lucide-react';
import { getProduct, updateProduct } from '../../../services/api/products';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import useFormData from './hooks/useFormData';
import BasicInfoTab from './components/BasicInfoTab';
import PricingTab from './components/PricingTab';
import AttributesTab from './components/AttributesTab';
import VariationsTab from './components/VariationsTab';
import ImagesTab from './components/ImagesTab';
import DropshippingSection from '../components/DropshippingSection';
import BundleEditor from '../../../components/product/BundleEditor/BundleEditor';
import { serializeBundleItems } from '../../../components/product/BundleEditor/bundleUtils';
import DropshippingVariationsSection from '../components/DropshippingVariationsSection';
import { getProviderStatus } from '../../../services/api/dropshipping';
import styles from './EditProductPage.module.css';
import { hasPrice } from '../../../utils/priceUtils';

const EditProductPage = () => {
  const navigate = useNavigate();
  const { productId: productId } = useParams();
  const { t } = useLocalization();
  const { success, error } = useAlert();

  useEffect(() => {
    getProviderStatus().then(setProviderStatus).catch(() => {});
  }, []);
  const [isLoading, setIsLoading] = useState(false);
  const [productNotFound, setProductNotFound] = useState(false);
  const [providerStatus, setProviderStatus] = useState(null);
  const [dropshippingData, setDropshippingData] = useState({
    dropshippingEnabled: false,
    minimumSellingPrice: '',
    maximumSellingPrice: '',
    dropshippingPrice: '',
  });
  
  const {
    // State
    formData,
    originalProduct,
    attributes,
    shops,
    warehouses,
    errors,
    isLoadingData,
    isLoadingProduct,
    activeTab,
    variationCombinations,
    expandedVariations,
    selectedVariations,
    showBulkActions,
    bulkShopAssignments,
    bulkPrices,
    bulkCostPrice,
    bulkWeight,
    skuValidation,

    // Setters
    setFormData,
    setErrors,
    setActiveTab,
    setShowBulkActions,
    setBulkCostPrice,
    setBulkWeight,

    // Utility functions
    getAvailableTabs,
    validateForm,
    hasFieldChanged,
    buildImageOperations,
    getNewMainImage,
    getNewGalleryImages,
    hasShopDataChanged,
    hasVariationsChanged,
    hasVariationShopDataChanged,

    // Handler functions
    handleInputChange,
    handleDescriptionChange,
    handleBundleChange,
    handleAttributesChange,
    handleAttributeValuesChange,
    handleCategorySelect,
    handleTagsChange,
    handleBrandChange,
    handleShopDataChange,
    handleOpeningStockChange,
    handleVariationChange,
    handleVariationShopDataChange,
    handleVariationStockChange,
    handleVariationImageChange,
    handleImageUpload,
    handleImageRemove,
    handleSetMainImage,

    // Variation management
    toggleVariationExpansion,
    toggleVariationSelection,
    selectAllVariations,
    deselectAllVariations,
    removeSelectedVariations,
    regenerateVariations,

    // Bulk actions
    handleBulkShopAssignment,
    handleBulkPriceChange,
    applyBulkActions,

    // Dropshipping variation handlers
    handleVariationDropshippingChange,
    applyBulkDropshipping,
  } = useFormData(productId);

  const showDropshippingTab = providerStatus?.isProviderEnabled && providerStatus?.kycStatus === 'approved';

  // Populate dropshipping state for simple products when product loads
  useEffect(() => {
    if (originalProduct && showDropshippingTab && originalProduct.type !== 'variable') {
      setDropshippingData({
        dropshippingEnabled: !!originalProduct.dropshippingEnabled,
        minimumSellingPrice: originalProduct.minimumSellingPrice ?? '',
        maximumSellingPrice: originalProduct.maximumSellingPrice ?? '',
        dropshippingPrice: originalProduct.dropshippingPrice ?? '',
      });
    }
  }, [originalProduct, showDropshippingTab]);

  // Available tabs based on product type
  const isBundle = formData.productType === 'bundle';
  const availableTabs = isBundle
    ? [
      { id: 'basic', label: t('products.basicInfo'), icon: Info },
      { id: 'bundle', label: t('products.bundleTab'), icon: Package },
    ]
    : [
      { id: 'basic', label: t('products.basicInfo'), icon: Info },
      { id: 'pricing', label: t('products.pricing'), icon: DollarSign },
      { id: 'attributes', label: t('products.attributes'), icon: Tag },
    ];

  if (formData.productType === 'variable') {
    availableTabs.push({ id: 'variations', label: 'Variations', icon: Settings });
  }

  availableTabs.push({ id: 'images', label: t('products.images'), icon: ImageIcon });

  if (showDropshippingTab && !isBundle) {
    availableTabs.push({ id: 'dropshipping', label: t('dropshipping.dropshipping'), icon: Settings });
  }

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0 && files[0].type.startsWith('image/')) {
      handleImageUpload(index, files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const submitData = new FormData();
      
      // ONLY include fields that have actually changed
      if (hasFieldChanged('name')) {
        submitData.append('name', formData.name.trim());
      }

      if (hasFieldChanged('printableName')) {
        submitData.append('printableName', formData.printableName.trim());
      }

      if (hasFieldChanged('description')) {
        submitData.append('description', formData.description.trim());
      }
      
      if (hasFieldChanged('type')) {
        submitData.append('type', formData.productType);
      }
      
      if (hasFieldChanged('visibility')) {
        submitData.append('visibility', formData.visibility);
      }
      
      if (hasFieldChanged('costPrice')) {
        submitData.append('costPrice', parseFloat(formData.buyingPrice));
      }
      
      if (hasFieldChanged('sku')) {
        submitData.append('sku', formData.sku.trim());
      }
      
      if (hasFieldChanged('barcode')) {
        submitData.append('barcode', formData.barcode.trim());
      }
      
      if (hasFieldChanged('brandId')) {
        submitData.append('brandId', parseInt(formData.brandId));
      }
      
      if (hasFieldChanged('weight')) {
        submitData.append('weight', parseFloat(formData.weight));
      }

      // Handle dimensions only if changed
      if (hasFieldChanged('dimensions')) {
        const dimensions = {};
        if (formData.dimensions.length) dimensions.length = parseFloat(formData.dimensions.length);
        if (formData.dimensions.width) dimensions.width = parseFloat(formData.dimensions.width);
        if (formData.dimensions.height) dimensions.height = parseFloat(formData.dimensions.height);
        if (Object.keys(dimensions).length > 0) {
          submitData.append('dimensions', JSON.stringify(dimensions));
        }
      }
      
      // Categories and tags only if changed
      if (hasFieldChanged('categoryId')) {
        submitData.append('categoryIds', JSON.stringify([parseInt(formData.categoryId)]));
      }
      
      if (hasFieldChanged('tagIds')) {
        submitData.append('tagIds', JSON.stringify(formData.tagIds));
      }
      
      if (hasFieldChanged('attributeIds')) {
        submitData.append('attributeIds', JSON.stringify(formData.attributeIds));
      }

      if (hasFieldChanged('attributeValueIds')) {
        submitData.append('attributeValueIds', JSON.stringify(formData.attributeValueIds));
      }
      
      // Handle image operations
      const imageOperations = buildImageOperations();
      if (imageOperations.deleteImages.length > 0 || imageOperations.setMainImageFromGallery) {
        submitData.append('imageOperations', JSON.stringify(imageOperations));
      }
      
      // Add new images
      const newMainImage = getNewMainImage();
      if (newMainImage) {
        submitData.append('mainImage', newMainImage);
      }
      
      const newGalleryImages = getNewGalleryImages();
      newGalleryImages.forEach(image => {
        submitData.append('galleryImages', image);
      });
      
      if (isBundle) {
        if (hasFieldChanged('bundleItems')) {
          submitData.append('bundleItems', JSON.stringify(serializeBundleItems(formData.bundleItems, formData.bundleShopIds)));
        }
      } else if (formData.productType === 'variable' && hasFieldChanged('variations')) {
        const validVariations = formData.variations.map((variation, index) => {
          const variationData = {
            // Include ID for existing variations, omit for new ones
            ...(variation.id && { id: variation.id }),
            sku: variation.sku || '',
            attributeValueIds: variation.attributeValueIds,
            costPrice: variation.costPrice === '' || variation.costPrice == null ? null : parseFloat(variation.costPrice),
            barcode: variation.barcode?.toString().trim() || null,
            weight: variation.weight === '' || variation.weight == null ? null : parseFloat(variation.weight),
            shopData: variation.shopData
              .filter(shop => shop.enabled && hasPrice(shop.salesPrice))
              .map(shop => {
                const shopData = {
                  shopId: shop.shopId,
                  salesPrice: parseFloat(shop.salesPrice),
                  offers: shop.offers || [],
                };
                if (shop.dropshippingPrice) {
                  shopData.dropshippingPrice = parseFloat(shop.dropshippingPrice);
                }
                if (shop.overrideDeliveryChargeEnabled && shop.overrideDeliveryCharge != null && shop.overrideDeliveryCharge !== '') {
                  shopData.overrideDeliveryCharge = parseFloat(shop.overrideDeliveryCharge);
                } else {
                  shopData.overrideDeliveryCharge = null;
                }
                return shopData;
              }),
            // Include deleteImage flag if variation image should be deleted
            ...(variation.deleteImage && { deleteImage: true }),
          };

          // Dropshipping fields per variation (only when provider enabled)
          if (showDropshippingTab) {
            variationData.dropshippingEnabled = !!variation.dropshippingEnabled;
            if (variation.minimumSellingPrice !== '') {
              variationData.minimumSellingPrice = parseFloat(variation.minimumSellingPrice) || null;
            }
            if (variation.maximumSellingPrice !== '') {
              variationData.maximumSellingPrice = parseFloat(variation.maximumSellingPrice) || null;
            }
            if (variation.dropshippingPrice !== '') {
              variationData.dropshippingPrice = parseFloat(variation.dropshippingPrice) || null;
            }
          }

          return variationData;
        });
        
        submitData.append('variations', JSON.stringify(validVariations));

        // Add new variation images
        formData.variations.forEach((variation, index) => {
          if (variation.variationImage && variation.variationImage instanceof File) {
            submitData.append(`variationImages[${index}]`, variation.variationImage);
          }
        });

      } else if (formData.productType === 'simple' && hasFieldChanged('shopData')) {
        // Handle simple product shop data
        const validShopData = formData.shopData
          .filter(shop => shop.enabled && hasPrice(shop.salesPrice))
          .map(shop => {
            const shopData = {
              shopId: shop.shopId,
              salesPrice: parseFloat(shop.salesPrice),
              offers: shop.offers || [],
            };
            if (shop.dropshippingPrice) {
              shopData.dropshippingPrice = parseFloat(shop.dropshippingPrice);
            }
            if (shop.overrideDeliveryChargeEnabled && shop.overrideDeliveryCharge != null && shop.overrideDeliveryCharge !== '') {
              shopData.overrideDeliveryCharge = parseFloat(shop.overrideDeliveryCharge);
            } else {
              shopData.overrideDeliveryCharge = null;
            }
            return shopData;
          });
        if (validShopData.length > 0) {
          submitData.append('shopData', JSON.stringify(validShopData));
        }
      }

      if (showDropshippingTab && formData.productType === 'simple') {
        submitData.append('dropshippingEnabled', dropshippingData.dropshippingEnabled ? 'true' : 'false');
        if (dropshippingData.minimumSellingPrice !== '') {
          submitData.append('minimumSellingPrice', parseFloat(dropshippingData.minimumSellingPrice));
        }
        if (dropshippingData.maximumSellingPrice !== '') {
          submitData.append('maximumSellingPrice', parseFloat(dropshippingData.maximumSellingPrice));
        }
        if (dropshippingData.dropshippingPrice !== '') {
          submitData.append('dropshippingPrice', parseFloat(dropshippingData.dropshippingPrice));
        }
      }

      const response = await updateProduct(productId, submitData);
      success(t('products.productUpdated'));
      navigate('/products');
      
    } catch (err) {
      console.error('Failed to update product:', err);
      setErrors({ submit: err.response?.data?.message || t('products.failedToUpdateProduct') });
    } finally {
      setIsLoading(false);
    }
  };

  // Product not found state
  if (productNotFound) {
    return (
      <div className={styles.editProductPage}>
        <div className={styles.pageHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <Package className={styles.headerIcon} size={24} />
              <div className={styles.titleContent}>
                <h1>{t('products.editProduct')}</h1>
                <p>Product not found</p>
              </div>
            </div>
            <div className={styles.headerActions}>
              <button 
                onClick={() => navigate('/products')} 
                className={styles.backButton}
              >
                <ArrowLeft size={20} />
                {t('products.backToProducts')}
              </button>
            </div>
          </div>
        </div>

        <div className={styles.pageContent}>
          <div className={styles.errorState}>
            <AlertCircle size={48} className={styles.errorIcon} />
            <h3>Product Not Found</h3>
            <p>The product you're looking for doesn't exist or has been removed.</p>
            <button 
              onClick={() => navigate('/products')}
              className={styles.backToProductsButton}
            >
              {t('products.backToProducts')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoadingProduct || isLoadingData) {
    return (
      <div className={styles.editProductPage}>
        <div className={styles.pageHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <Package className={styles.headerIcon} size={24} />
              <div className={styles.titleContent}>
                <h1>{t('products.editProduct')}</h1>
                <p>Loading product details...</p>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.pageContent}>
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinnerLarge}></div>
            <h3>{t('products.loadingData')}</h3>
            <p>{t('products.loadingDataDesc')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.editProductPage}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <Package className={styles.headerIcon} size={24} />
            <div className={styles.titleContent}>
              <h1>{t('products.editProduct')}</h1>
              <p>Update product information and settings</p>
            </div>
          </div>
          <div className={styles.headerActions}>
            <button 
              onClick={() => navigate('/products')} 
              className={styles.backButton}
            >
              <ArrowLeft size={20} />
              {t('products.backToProducts')}
            </button>
          </div>
        </div>
      </div>

      <div className={styles.pageContent}>
        {errors.submit && (
          <div className={styles.errorBanner}>
            <AlertCircle size={20} />
            <div>
              <p>{errors.submit}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.productForm}>
          {/* Navigation Tabs */}
          <div className={styles.tabNavigation}>
            {availableTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`${styles.tabButton} ${activeTab === tab.id ? styles.active : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className={styles.tabContent}>
            {activeTab === 'basic' && (
              <BasicInfoTab
                formData={formData}
                errors={errors}
                handleInputChange={handleInputChange}
                handleDescriptionChange={handleDescriptionChange}
                handleCategorySelect={handleCategorySelect}
                handleTagsChange={handleTagsChange}
                handleBrandChange={handleBrandChange}
                isEdit={true}
                originalType={originalProduct?.type}
              />
            )}
            
            {activeTab === 'pricing' && (
              <PricingTab
                formData={formData}
                shops={shops}
                warehouses={warehouses}
                errors={errors}
                skuValidation={skuValidation}
                handleInputChange={handleInputChange}
                handleShopDataChange={handleShopDataChange}
                handleOpeningStockChange={handleOpeningStockChange}
                isEdit={true}
                productId={productId}
              />
            )}
            
            {activeTab === 'attributes' && (
              <AttributesTab
                formData={formData}
                errors={errors}
                onAttributesChange={handleAttributesChange}
                onAttributeValuesChange={handleAttributeValuesChange}
                isEdit={true}
              />
            )}
            
            {activeTab === 'variations' && (
              <VariationsTab
                formData={formData}
                attributes={attributes}
                shops={shops}
                warehouses={warehouses}
                errors={errors}
                skuValidation={skuValidation}
                variationCombinations={variationCombinations}
                expandedVariations={expandedVariations}
                selectedVariations={selectedVariations}
                showBulkActions={showBulkActions}
                bulkShopAssignments={bulkShopAssignments}
                bulkPrices={bulkPrices}
                bulkCostPrice={bulkCostPrice}
                bulkWeight={bulkWeight}
                setActiveTab={setActiveTab}
                toggleVariationExpansion={toggleVariationExpansion}
                toggleVariationSelection={toggleVariationSelection}
                selectAllVariations={selectAllVariations}
                deselectAllVariations={deselectAllVariations}
                removeSelectedVariations={removeSelectedVariations}
                regenerateVariations={regenerateVariations}
                handleBulkShopAssignment={handleBulkShopAssignment}
                handleBulkPriceChange={handleBulkPriceChange}
                setBulkCostPrice={setBulkCostPrice}
                setBulkWeight={setBulkWeight}
                setShowBulkActions={setShowBulkActions}
                applyBulkActions={() => applyBulkActions(success, error)}
                handleVariationChange={handleVariationChange}
                handleVariationShopDataChange={handleVariationShopDataChange}
                handleVariationStockChange={handleVariationStockChange}
                handleVariationImageChange={handleVariationImageChange}
                isEdit={true}
                productId={productId}
              />
            )}
            
            {activeTab === 'bundle' && isBundle && (
              <BundleEditor
                bundleItems={formData.bundleItems}
                bundleShopIds={formData.bundleShopIds}
                shops={shops}
                bundleProductId={productId}
                errors={errors}
                onChange={handleBundleChange}
                formData={formData}
                skuValidation={skuValidation}
                handleInputChange={handleInputChange}
              />
            )}

            {activeTab === 'images' && (
              <ImagesTab
                images={formData.images}
                mainImageIndex={formData.mainImageIndex}
                onImageUpload={handleImageUpload}
                onImageRemove={handleImageRemove}
                onSetMainImage={handleSetMainImage}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                errors={errors}
                isEdit={true}
              />
            )}

            {activeTab === 'dropshipping' && showDropshippingTab && (
              formData.productType === 'variable' ? (
                <DropshippingVariationsSection
                  variations={formData.variations}
                  onVariationDropshippingChange={handleVariationDropshippingChange}
                  onBulkApply={applyBulkDropshipping}
                />
              ) : (
                <DropshippingSection
                  dropshippingEnabled={dropshippingData.dropshippingEnabled}
                  minimumSellingPrice={dropshippingData.minimumSellingPrice}
                  maximumSellingPrice={dropshippingData.maximumSellingPrice}
                  dropshippingPrice={dropshippingData.dropshippingPrice}
                  onChange={(field, value) => setDropshippingData((prev) => ({ ...prev, [field]: value }))}
                />
              )
            )}
          </div>

          {/* Form Actions */}
          <div className={styles.formActions}>
            <button 
              type="button" 
              onClick={() => navigate('/products')}
              className={styles.cancelButton}
            >
              <X size={18} />
              {t('common.cancel')}
            </button>
            <button 
              type="submit" 
              className={styles.submitButton}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className={styles.loadingSpinner}></div>
              ) : (
                <>
                  <Save size={18} />
                  {t('products.updateProduct')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProductPage;